import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, defer, of, Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { NotificationService } from '../../../services/notification.service';
import { blank, data_get, filled, observe, OptionalValue, spf, str_len, transform } from '../../../shared/utils/common';
import { ChecklistsService } from '../../../services/checklist.service';
import { DIALOG_DATA, DialogService } from '../../../services/dialog.service';
import { FormControl, FormGroup } from '@angular/forms';
import { CHECKLIST_TYPES } from '../../../module/checklists/shared/constants';
import { FieldmagicDropdownOption } from '../../../shared/components/forms/input/dropdown/dropdown.component';
import {
  ChecklistsGenerateWithArtificationIntelligenceGeneratedEvent,
  ChecklistType,
} from '../../../module/checklists/';

@Component({
  selector: 'app-create-checklist',
  template: `
    <fieldmagic-dialog
      icon="plus-circle"
      label="add_checklist"
      [instance]="self"
      [disableClose]="saving$ | async"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <convert-pdf-with-ai-button
          (uploaded)="onGenerate($event)"
          [moduleOrigin]="'checklist'"
          [purpose]="'pdf_checklist_ai'"
          [data]="{
            checklist_type: form.value | data_get: 'checklist_type.id',
            asset_type_id: form.value | data_get: 'asset_type_id.id',
            name: form.value | data_get: 'name',
            specifics: specifics,
            mode: 'add'
          }"
        >
        </convert-pdf-with-ai-button>
        <checklists-generate-with-ai-button
          (generated)="onGenerate($event)"
          [data]="{
            checklist_type: form.value | data_get: 'checklist_type.id',
            asset_type_id: form.value | data_get: 'asset_type_id.id',
            name: form.value | data_get: 'name',
            specifics: specifics
          }"
        >
        </checklists-generate-with-ai-button>

        <fieldmagic-primary-button
          purpose="save"
          [isInProgress]="saving$ | async"
          (click)="onSave()"
        >
        </fieldmagic-primary-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <div
          fieldmagicGridRow
          [formGroup]="form"
        >
          <div
            fieldmagicGridColumn="12"
          >
            <fieldmagic-text-input
              formControlName="name"
              [errors]="errors | data_get: 'name'"
              label="Name"
              [standalone]="false"
              [withRequiredMarker]="true"
            >
            </fieldmagic-text-input>
          </div>

          <div
            fieldmagicGridColumn="12"
          >
            <fieldmagic-dropdown-input
              formControlName="checklist_type"
              [errors]="errors | data_get: 'checklist_type'"
              label="Checklist Type"
              [withRequiredMarker]="true"
              [options]="types$ | async"
            >
            </fieldmagic-dropdown-input>
          </div>

          <div
            *ngIf="shouldShowAssetTypesField$ | async"
            fieldmagicGridColumn="12"
          >
            <fieldmagic-relate-input
              label="Asset Type"
              formControlName="asset_type_id"
              moduleName="asset_types"
              [withRequiredMarker]="true"
              [errors]="errors | data_get: 'asset_type_id'"
              [withServerAdditionalFilter]="false"
            >
            </fieldmagic-relate-input>
          </div>

        </div>
      </ng-template>
    </fieldmagic-dialog>
  `,
  styleUrls: ['./create-checklist.component.scss'],
  providers: [
    DialogService,
  ],
})
export class CreateChecklistComponent implements OnInit, OnDestroy {
  readonly form = new FormGroup({
    name: new FormControl(),
    checklist_type: new FormControl(transform(this._data.checklist_type, {
      transformer: (preselected) => ({
        id: this._data.checklist_type,
        text: `${preselected}_checklist`,
      }),
    })),
    asset_type_id: new FormControl(),
  });

  errors?: Record<string, string[]>;

  readonly saving$ = new BehaviorSubject<boolean>(false);
  readonly generating$ = new BehaviorSubject<boolean>(false);

  readonly types$ = new BehaviorSubject<FieldmagicDropdownOption[]>(CHECKLIST_TYPES.map((option) => ({
    id: option,
    text: `${option}_checklist`,
  })));

  readonly shouldShowAssetTypesField$ = new BehaviorSubject<boolean>(this._data.checklist_type == 'asset');

  get self() {
    return this;
  }

  get specifics(): OptionalValue<string> {
    const type: OptionalValue<ChecklistType> = data_get(this.form.value, 'checklist_type.id');

    if (blank(type) || type != 'asset') {
      return null;
    }

    const standard = data_get(this.form.value, 'asset_type_id.applicable_standards');

    if (blank(standard)) {
      return null;
    }

    return spf('Use %s standard', {
      args: [standard],
    });
  }

  private _onChecklistTypeChange?: Subscription;

  constructor(
    private readonly _checklists: ChecklistsService,
    private readonly _notifications: NotificationService,
    private readonly _router: Router,
    private readonly _dialog: DialogService,
    @Inject(DIALOG_DATA) private readonly _data: CreateChecklistDialogData,
  ) { }

  ngOnInit(): void {
    this._onChecklistTypeChange = this.form.get('checklist_type').valueChanges
      .subscribe((selected: FieldmagicDropdownOption) => this.shouldShowAssetTypesField$.next(selected.id == 'asset'));
  }

  ngOnDestroy(): void {
    if (this._onChecklistTypeChange) {
      this._onChecklistTypeChange.unsubscribe();
    }
  }

  onSave = () => observe({
    before: () => this.saving$.next(true),
    after: () => this.saving$.next(false),
    observable: () => defer(() => {
      if (! this._isValid()) {
        return of(null);
      }

      const values = this.form.value;

      return this._checklists.create({
        name: data_get(values, 'name'),
        type: data_get(values, 'checklist_type.id'),
        asset_type_id: data_get(values, 'asset_type_id.id'),
      });
    }),
  }).pipe(
    filter((id) => filled(id)),
    tap(() => this._notifications.notifySuccess('checklist_created_success')),
  ).subscribe((id) => {
    this._dialog.close({
      instance: this,
    });

    this._router.navigate(['/checklists/edit', id]);
  });

  onClose = () => this._dialog.close({
    instance: this,
  });

  onGenerate(event: ChecklistsGenerateWithArtificationIntelligenceGeneratedEvent): void {
    if (blank(event)) {
      return;
    }

    this._dialog.close({
      instance: this,
    });

    this._router.navigate(['/checklists/edit', event.id]);
  }

  private _isValid(): boolean {
    this.errors = {};

    const values = this.form.value;

    if (blank(data_get(values, 'name'))) {
      this.errors['name'] = ['field_is_required'];
    }

    if (str_len(data_get(values, 'name')) > 128) {
      this.errors['name'] = ['This field should not exceed 128 characters.'];
    }

    if (blank(data_get(values, 'checklist_type'))) {
      this.errors['checklist_type'] = ['field_is_required'];
    }

    if (data_get(values, 'checklist_type.id') == 'asset'
      && blank(data_get(values, 'asset_type_id'))
    ) {
      this.errors['asset_type_id'] = ['field_is_required'];
    }

    return blank(this.errors);
  }
}

export type CreateChecklistDialogData = {
  checklist_type?: string;
}